/**
 * Canonical ABP balance type values used across URL params and backend payloads.
 */
export const ABP_BALANCE_TYPES = {
  ON_ACCOUNT: 'onAccount',
  LAYBY: 'layby',
} as const

export type AbpBalanceType = typeof ABP_BALANCE_TYPES[keyof typeof ABP_BALANCE_TYPES]

type AbpBalanceSource = {
  account: number
  onAccountBalance?: number
  laybyBalance?: number
}

/**
 * Validates a route/query value and returns an ABP balance type.
 * Unknown values are treated as unset so backend keeps legacy behavior.
 */
export const resolveAbpBalanceType = (
  balanceType: string | null | undefined
): AbpBalanceType | null => {
  if (balanceType === ABP_BALANCE_TYPES.ON_ACCOUNT) {
    return ABP_BALANCE_TYPES.ON_ACCOUNT
  }

  if (balanceType === ABP_BALANCE_TYPES.LAYBY) {
    return ABP_BALANCE_TYPES.LAYBY
  }

  return null
}

/**
 * Returns the outstanding balance (as a positive number) for the selected ABP balance type.
 *
 * Outstanding balance is modeled as negative debt values in sale/customer balance data.
 * This helper normalizes the selected balance bucket into a non-negative amount that can
 * safely be used for UI caps and payment validation.
 *
 * Selection rules:
 * - `onAccount` → `onAccountBalance`
 * - `layby` → `laybyBalance`
 * - unset/legacy → combined `account`
 *
 * Any missing or non-debt value resolves to `0`.
 */
export const getAbpOutstandingBalance = (
  customerBalances: AbpBalanceSource | null | undefined,
  balanceType: AbpBalanceType | null | undefined
): number => {
  if (!customerBalances) {
    return 0
  }

  const balanceKey =
    balanceType === ABP_BALANCE_TYPES.ON_ACCOUNT
      ? 'onAccountBalance'
      : balanceType === ABP_BALANCE_TYPES.LAYBY
      ? 'laybyBalance'
      : 'account'

  const selectedBalance = customerBalances[balanceKey] ?? 0

  return Math.max(-selectedBalance, 0)
}

/**
 * Caps a candidate ABP amount to the selected balance type's outstanding balance.
 *
 * Note: `getAbpOutstandingBalance` only returns the max payable amount.
 * This helper adds input guardrails (finite/positive checks) and performs
 * the final clamp in one place for ABP flows.
 *
 * Invalid, non-finite, zero, or negative amounts return `0`.
 * Valid positive amounts are clamped to the selected outstanding debt bucket.
 */
export const resolveAbpCappedAmount = (
  amount: number,
  customerBalances: AbpBalanceSource | null | undefined,
  balanceType: AbpBalanceType | null | undefined
): number => {
  if (!Number.isFinite(amount) || amount <= 0) {
    return 0
  }

  return Math.min(
    amount,
    getAbpOutstandingBalance(customerBalances, balanceType)
  )
}
